import { cn } from "@/lib/utils";
import * as React from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";

type SheetContextTypes = {
  onOpenChange?: (open: boolean) => void;
};

const SheetContext = React.createContext<SheetContextTypes>({});

const FullScreenSheet = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode;
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
  }
>(({ children, isOpen, onOpenChange }, ref) => {
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen) {
      onOpenChange(true);
    }
  };

  return (
    <div ref={ref}>
      <SheetContext.Provider value={{ onOpenChange }}>
        <SheetPrimitive.Root
          open={isOpen}
          onOpenChange={handleOpenChange}
          modal={false}
        >
          {children}
        </SheetPrimitive.Root>
      </SheetContext.Provider>
    </div>
  );
});
FullScreenSheet.displayName = "FullScreenSheet";

const FullScreenSheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content> & {
    hasCloseButton?: boolean;
  }
>(({ className, children, hasCloseButton = true, ...props }, ref) => {
  const { onOpenChange } = React.useContext(SheetContext);

  return (
    <SheetPrimitive.Content
      ref={ref}
      className={cn(
        "w-screen h-[94vh] fixed gap-4 bg-background p-6 inset-x-0 bottom-0 border-t",
        className,
      )}
      {...props}
    >
      {children}
      <SheetPrimitive.Close
        className={cn("absolute right-4 top-4", !hasCloseButton && "sr-only")}
        onClick={(e) => {
          e.stopPropagation();
          onOpenChange && onOpenChange(false);
        }}
      >
        <Cross2Icon className="h-4 w-4" />
        <span className="sr-only">Close</span>
      </SheetPrimitive.Close>
    </SheetPrimitive.Content>
  );
});
FullScreenSheetContent.displayName = "FullScreenSheetContent";

export { FullScreenSheet, FullScreenSheetContent };
